// hooks/useSubscriptions.js
import { useState, useCallback } from 'react';
import { App } from 'antd';
import { getUserProfile, getSubscriptions, getCatalogueNames, calculateSkuUsed, calculateGbUsed } from '../api/subscriptionApi';

export const useSubscriptions = () => {
    const { message: messageApi } = App.useApp();
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [subscriptionsCache, setSubscriptionsCache] = useState({
        active: [],
        expired: [],
        lastFetched: null,
        userProfile: null
    });

    // Function to filter subscriptions based on catalogues (same logic as DataConsumption)
    const filterSubscriptionsByCatalogues = (subscriptions, userCatalogues, userRole) => {
        if (userRole === "Admin") {
            return subscriptions; // Admin sees everything
        }

        // If user has no catalogues, return empty array
        if (!userCatalogues || userCatalogues.length === 0) {
            return [];
        }

        const filtered = subscriptions.filter(sub => {
            const subCatalogues = sub.list_catalogues?.list_catalogues_refs || [];
            // Check if there's at least one catalogue in common
            return subCatalogues.some(catalogId => userCatalogues.includes(catalogId));
        });

        return filtered;
    };

    // Function to group subscriptions by main/add-ons (same logic as DataConsumption)
    const groupSubscriptions = (subscriptions, type) => {
        const targetStatus = type === 'active' ? ['active'] : ['expired', 'renewed'];

        // 1. Primo livello: tutte le isMainSubscription === true con status corrispondente
        const mainSubscriptions = subscriptions.filter(sub => {
            const isMain = sub.isMainSubscription === true;
            const hasTargetStatus = targetStatus.includes((sub.status || '').toLowerCase());
            return isMain && hasTargetStatus;
        });

        // 2. Secondo livello: tutte le isMainSubscription === false con status corrispondente
        //    che hanno mainSubscriptionRef corrispondente agli ID del primo livello
        const addOns = subscriptions.filter(sub => {
            const isAddOn = sub.isMainSubscription === false;
            const hasTargetStatus = targetStatus.includes((sub.status || '').toLowerCase());
            const hasMainRef = sub.mainSubscriptionRef && sub.mainSubscriptionRef.trim() !== "";
            const mainExists = hasMainRef && mainSubscriptions.some(main => main.id === sub.mainSubscriptionRef.trim());

            return isAddOn && hasTargetStatus && hasMainRef && mainExists;
        });

        // 3. Subscription Additional "orfane" (senza main subscription corrispondente)
        const orphanedAddOns = subscriptions.filter(sub => {
            const isAddOn = sub.isMainSubscription === false;
            const hasTargetStatus = targetStatus.includes((sub.status || '').toLowerCase());
            const hasMainRef = sub.mainSubscriptionRef && sub.mainSubscriptionRef.trim() !== "";
            const mainExists = hasMainRef && mainSubscriptions.some(main => main.id === sub.mainSubscriptionRef.trim());

            // Additional senza mainSubscriptionRef o con mainSubscriptionRef che non esiste
            const noMainRef = !hasMainRef;
            const mainNotExists = hasMainRef && !mainExists;

            return isAddOn && hasTargetStatus && (noMainRef || mainNotExists);
        });

        // 4. Creare la struttura finale
        const groupedData = [];
        let keyCounter = 0; // Counter per garantire chiavi uniche

        // Aggiungere le sottoscrizioni principali
        mainSubscriptions.forEach(mainSub => {
            groupedData.push({
                ...mainSub,
                level: 1,
                isMainSubscription: true,
                key: `main-${mainSub.id}-${keyCounter++}` // Chiave unica
            });

            // Aggiungere gli add-on relativi a questa sottoscrizione principale
            const relatedAddOns = addOns.filter(addOn =>
                addOn.mainSubscriptionRef.trim() === mainSub.id
            );

            relatedAddOns.forEach(addOn => {
                groupedData.push({
                    ...addOn,
                    level: 2,
                    isMainSubscription: false,
                    key: `addon-${addOn.id}-${keyCounter++}` // Chiave unica
                });
            });
        });

        // Aggiungere le subscription Additional "orfane" come livello 1
        orphanedAddOns.forEach(orphanedSub => {
            groupedData.push({
                ...orphanedSub,
                level: 1,
                isMainSubscription: false,
                isOrphaned: true, // Flag per identificare le subscription orfane
                key: `orphaned-${orphanedSub.id}-${keyCounter++}` // Chiave unica
            });
        });

        return groupedData;
    };

    const fetchSubscriptions = useCallback(async (subscriptionType, forceRefresh = false) => {
        try {
            const currentTime = Date.now();
            const cacheExpiry = 60000; // 1 minuto di cache
            const isCacheValid = subscriptionsCache.lastFetched &&
                (currentTime - subscriptionsCache.lastFetched < cacheExpiry) &&
                !forceRefresh;

            // Se la cache è valida e abbiamo i dati per il tipo richiesto, utilizzali
            if (isCacheValid && subscriptionsCache[subscriptionType]) {
                setSubscriptions(subscriptionsCache[subscriptionType]);
                setUserRole(subscriptionsCache.userProfile?.role || null);
                setLoading(false);
                return;
            }

            setLoading(true);

            // 1. Get user profile (cache it to avoid repeated calls)
            let userProfile = subscriptionsCache.userProfile;
            if (!userProfile || forceRefresh) {
                userProfile = await getUserProfile();
            }
            const { clientRef, list_catalogues: userCatalogues = [], role } = userProfile;

            // Set user role
            setUserRole(role);

            // 2. Fetch both active and expired subscriptions to populate cache
            const activeStatusFilter = ["Active"];
            const expiredStatusFilter = ["Expired", "Renewed"]; // Renewed vanno nello storico

            const [activeSubscriptions, expiredSubscriptions] = await Promise.all([
                getSubscriptions(clientRef, role, activeStatusFilter),
                getSubscriptions(clientRef, role, expiredStatusFilter)
            ]);

            // 3. Process active subscriptions
            const activeFilteredSubscriptions = filterSubscriptionsByCatalogues(
                activeSubscriptions,
                userCatalogues,
                role
            );

            // 4. Process expired subscriptions  
            const expiredFilteredSubscriptions = filterSubscriptionsByCatalogues(
                expiredSubscriptions,
                userCatalogues,
                role
            );

            // 5. Get catalogue names for display (combine both active and expired)
            const allCatalogueIds = [
                ...new Set([
                    ...userCatalogues,
                    ...activeFilteredSubscriptions.flatMap(sub => sub.list_catalogues?.list_catalogues_refs || []),
                    ...expiredFilteredSubscriptions.flatMap(sub => sub.list_catalogues?.list_catalogues_refs || [])
                ])
            ];
            const catalogueNames = await getCatalogueNames(allCatalogueIds);

            // 6. Process active subscriptions with SKU and GB
            const activeSubscriptionsProcessed = await Promise.all(
                activeFilteredSubscriptions.map(async (sub) => {
                    const catalogueRefs = sub.list_catalogues?.list_catalogues_refs || [];
                    const skuUsed = await calculateSkuUsed(catalogueRefs);
                    const gbUsed = await calculateGbUsed(sub);
                    return {
                        ...sub,
                        skuUsed,
                        gbUsed,
                        catalogueNames: (sub.list_catalogues?.list_catalogues_refs || []).map(id =>
                            catalogueNames[id] || id
                        ).join(", ")
                    };
                })
            );

            // 7. Process expired subscriptions with SKU and GB
            const expiredSubscriptionsProcessed = await Promise.all(
                expiredFilteredSubscriptions.map(async (sub) => {
                    const catalogueRefs = sub.list_catalogues?.list_catalogues_refs || [];
                    const skuUsed = await calculateSkuUsed(catalogueRefs);
                    const gbUsed = await calculateGbUsed(sub);
                    return {
                        ...sub,
                        skuUsed,
                        gbUsed,
                        catalogueNames: (sub.list_catalogues?.list_catalogues_refs || []).map(id =>
                            catalogueNames[id] || id
                        ).join(", ")
                    };
                })
            );

            // 8. Group subscriptions
            const activeGroupedSubscriptions = groupSubscriptions(activeSubscriptionsProcessed, 'active');
            const expiredGroupedSubscriptions = groupSubscriptions(expiredSubscriptionsProcessed, 'expired');

            // 9. Update cache
            setSubscriptionsCache({
                active: activeGroupedSubscriptions,
                expired: expiredGroupedSubscriptions,
                lastFetched: currentTime,
                userProfile: userProfile
            });

            // 10. Set the current subscriptions based on requested type
            const currentSubscriptions = subscriptionType === 'active' ? activeGroupedSubscriptions : expiredGroupedSubscriptions;
            setSubscriptions(currentSubscriptions);

        } catch (error) {
            console.error("❌ Error loading subscriptions:", error);
            messageApi.error("An error occurred whilst retrieving data. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [messageApi, subscriptionsCache]);

    // Function to get subscriptions from cache without API calls
    const getSubscriptionsFromCache = useCallback((subscriptionType) => {
        if (subscriptionsCache[subscriptionType]) {
            setSubscriptions(subscriptionsCache[subscriptionType]);
            setUserRole(subscriptionsCache.userProfile?.role || null);
        }
    }, [subscriptionsCache]);

    // Function to invalidate cache
    const invalidateCache = useCallback(() => {
        setSubscriptionsCache({
            active: [],
            expired: [],
            lastFetched: null,
            userProfile: null
        });
    }, []);

    return {
        subscriptions,
        setSubscriptions,
        loading,
        userRole,
        subscriptionsCache,
        fetchSubscriptions,
        getSubscriptionsFromCache,
        invalidateCache
    };
}; 